import React, { useCallback, useEffect, useState } from "react";

import { DiscardChangeCase } from "@/components/governance/DiscardChangeCase";
import {
  CHANGE_DRAFT_IDS,
  type ChangeDraftField,
  validateChangeDraft,
} from "@/components/portfolio/change-draft";
import {
  ACTIVE_CHANGE_CASE_STATUSES,
  CHANGE_KINDS,
  type ChangeKind,
} from "@/components/portfolio/change-kinds";
import { amendmentLabel, changeCaseStatusLabel } from "@/components/shared/governance-labels";
import {
  Alert,
  Badge,
  Button,
  countLabel,
  Divider,
  FormField,
  Heading,
  MutedText,
  Panel,
  RadioCardGroup,
  Text,
  TextAreaField,
  TextField,
  useConfirm,
} from "@/components/ui";
import { ChoiceBones } from "@/components/ui/SkeletonParts";
import type { ProgrammeGovernanceSummary, ProgrammePlatformClient } from "@/platform";
import { begin } from "@/utils/begin";
import { focusFirstProblem, problemsByField, withoutProblem } from "@/utils/field-problems";

/** Every control here refuses for one reason: a write is already running. */
const BUSY = "Wait for the change in progress to finish.";

export function PortfolioChangeManagement({
  client,
  programmeId,
  onWorkspaceOpen,
}: {
  client: ProgrammePlatformClient;
  programmeId: string;
  onWorkspaceOpen: (changeCaseId: string) => void;
}): React.JSX.Element {
  const confirm = useConfirm();
  const [governance, setGovernance] = useState<ProgrammeGovernanceSummary | null>(null);
  const [selection, setSelection] = useState<ChangeKind | null>(null);
  const [institutionalLabel, setInstitutionalLabel] = useState("");
  const [rationale, setRationale] = useState("");
  const [problems, setProblems] = useState<Partial<Record<ChangeDraftField, string>>>({});
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGovernance = useCallback(
    (afterWrite = false): Promise<void> =>
      begin(() => client.getProgrammeGovernance(programmeId))
        .then((result) => {
          setGovernance(result);
          setError(null);
        })
        .catch((cause: unknown) => {
          // A read that fails after a write leaves the old one describing a
          // programme that has moved on, so nothing it offers can stand.
          setGovernance(null);
          const message =
            cause instanceof Error ? cause.message : "Unable to load programme changes";
          setError(
            afterWrite
              ? `The change was saved, but the programme's changes could not be reloaded. ${message}`
              : message,
          );
        })
        .finally(() => setLoading(false)),
    [client, programmeId],
  );

  useEffect(() => {
    loadGovernance();
  }, [loadGovernance]);

  const activeChangeCase = governance?.curriculumChangeCases.find((changeCase) =>
    ACTIVE_CHANGE_CASE_STATUSES.some((status) => status === changeCase.status),
  );

  const runAction = async (action: () => Promise<unknown>): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      await action();
      await loadGovernance(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to update programme changes");
    } finally {
      setPending(false);
    }
  };

  const startChange = async (): Promise<void> => {
    if (!selection) {
      return;
    }
    const found = validateChangeDraft({ label: institutionalLabel, rationale });
    setProblems(problemsByField(found));
    if (found.length > 0) {
      focusFirstProblem(found, (field) => CHANGE_DRAFT_IDS[field]);
      return;
    }

    const input = {
      programmeId,
      institutionalLabel: institutionalLabel.trim(),
      rationale: rationale.trim(),
    };
    await runAction(async () => {
      if (selection === "revalidation") {
        await client.openRevalidation(input);
      } else {
        await client.openAmendment({ ...input, classification: selection });
      }
      setSelection(null);
      setInstitutionalLabel("");
      setRationale("");
    });
  };

  const confirmTeam = async (decision: "confirm" | "reset"): Promise<void> => {
    if (!activeChangeCase || activeChangeCase.status !== "team-confirmation") {
      return;
    }
    await runAction(() =>
      client.confirmChangeCaseTeam({
        programmeId,
        changeCaseId: activeChangeCase.id,
        decision,
      }),
    );
  };

  const resetTeam = (): void => {
    confirm({
      title: "Reset non-admin team?",
      body: "This removes non-admin programme responsibilities and module ownership. A new team can be assigned afterwards.",
      confirmLabel: "Reset team and continue",
      tone: "danger",
      "data-testid": "portfolio-reset-team-confirmation",
    }).then((confirmed) => {
      if (confirmed) {
        confirmTeam("reset");
      }
    });
  };

  const discard = activeChangeCase ? (
    <DiscardChangeCase
      changeCase={activeChangeCase}
      busy={pending}
      busyReason={BUSY}
      onDiscard={() =>
        runAction(() =>
          client.discardChangeCase({ programmeId, changeCaseId: activeChangeCase.id }),
        )
      }
      onWithdraw={() =>
        runAction(() =>
          client.withdrawChangeCase({ programmeId, changeCaseId: activeChangeCase.id }),
        )
      }
    />
  ) : null;

  return (
    <>
      <Divider className="mt-5" />
      <section className="pt-5" data-testid="portfolio-change-management">
        <div>
          <Heading level={3} size="md">
            Programme changes
          </Heading>
          <MutedText className="mt-1">
            Start work from this approved revision. The approved programme stays unchanged while its
            team works in a separate workspace.
          </MutedText>
        </div>

        {error ? (
          <Alert tone="error" className="mt-4">
            <span className="flex flex-wrap items-center gap-3">
              {error}
              {/* Without the open changes nothing here can be offered safely. */}
              {governance ? null : (
                <Button
                  size="sm"
                  variant="outline"
                  icon="arrow-clockwise"
                  onClick={() => {
                    setLoading(true);
                    setError(null);
                    loadGovernance();
                  }}
                >
                  Try again
                </Button>
              )}
            </span>
          </Alert>
        ) : null}

        {/* Mounted whatever the status: a live region inserted together with its
            text is commonly missed, so the region is there before it fills. */}
        <div className="mt-4 empty:mt-0" role="status">
          {loading ? (
            <>
              <span className="sr-only">Loading programme changes</span>
              <ChoiceBones count={4} />
            </>
          ) : null}
        </div>

        {!loading && governance && !activeChangeCase ? (
          <div className="mt-4">
            <RadioCardGroup<ChangeKind | "">
              legend="Type of programme change"
              value={selection ?? ""}
              onChange={(kind) => setSelection(kind === "" ? null : kind)}
              options={CHANGE_KINDS.map((option) => ({
                value: option.kind,
                label: option.label,
                icon: option.icon,
                description: option.description,
                disabled: pending,
                disabledReason: BUSY,
                "data-testid": option.testId,
              }))}
              data-testid="portfolio-change-type"
            />
          </div>
        ) : null}

        {selection && !activeChangeCase ? (
          <Panel
            tone="inset"
            headingLevel={4}
            title={`Start ${CHANGE_KINDS.find((option) => option.kind === selection)?.label ?? ""}`}
            className="mt-4"
          >
            <div className="flex flex-col gap-4">
              <FormField
                label="Institutional revision label"
                htmlFor="portfolio-change-label"
                required
                error={problems.label}
              >
                <TextField
                  id="portfolio-change-label"
                  value={institutionalLabel}
                  onChange={(value) => {
                    setInstitutionalLabel(value);
                    setProblems((current) => withoutProblem(current, "label"));
                  }}
                  required
                  disabled={pending}
                  disabledReason={BUSY}
                  data-testid="portfolio-change-label"
                />
              </FormField>
              <FormField
                label="Rationale for opening this change"
                htmlFor="portfolio-change-rationale"
                helpText="Describe the need for the change and the outcome the institution expects from it."
                required
                error={problems.rationale}
              >
                <TextAreaField
                  id="portfolio-change-rationale"
                  value={rationale}
                  onChange={(value) => {
                    setRationale(value);
                    setProblems((current) => withoutProblem(current, "rationale"));
                  }}
                  required
                  disabled={pending}
                  disabledReason={BUSY}
                  data-testid="portfolio-change-rationale"
                />
              </FormField>
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                icon="x"
                onClick={() => {
                  setSelection(null);
                  setProblems({});
                }}
                disabled={pending}
                disabledReason={BUSY}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                icon="folder-plus"
                onClick={() => startChange()}
                loading={pending}
                data-testid="portfolio-confirm-start-change"
              >
                Create change workspace
              </Button>
            </div>
          </Panel>
        ) : null}

        {activeChangeCase ? (
          <Alert tone="info" className="mt-4 [&>div]:w-full" data-testid="portfolio-active-change">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary" size="sm">
                {changeCaseStatusLabel(activeChangeCase.status)}
              </Badge>
              <Badge variant="outline" size="sm">
                {activeChangeCase.kind === "revalidation"
                  ? "Revalidation"
                  : amendmentLabel(activeChangeCase.classification)}
              </Badge>
              <span className="font-medium">{activeChangeCase.institutionalLabel}</span>
            </div>
            <Text tone="muted" className="mt-2">
              {activeChangeCase.rationale ?? "No opening rationale was recorded for this change."}
            </Text>

            {activeChangeCase.status === "team-confirmation" ? (
              <div data-testid="portfolio-team-confirmation">
                <Divider className="my-3" />
                <Text className="font-medium">Confirm the team for this change</Text>
                <Text tone="muted" className="mt-1">
                  {countLabel(
                    activeChangeCase.carriedTeam.responsibilities.length,
                    "programme responsibility",
                    "programme responsibilities",
                  )}{" "}
                  and{" "}
                  {countLabel(activeChangeCase.carriedTeam.moduleOwnership.length, "module owner")}{" "}
                  will be carried forward.
                </Text>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => confirmTeam("confirm")}
                    loading={pending}
                    data-testid="portfolio-confirm-carried-team"
                  >
                    Keep existing team
                  </Button>
                  <Button
                    variant="outline"
                    tone="danger"
                    size="sm"
                    onClick={resetTeam}
                    disabled={pending}
                    disabledReason={BUSY}
                    data-testid="portfolio-prepare-reset-team"
                  >
                    Reset non-admin team
                  </Button>
                  {discard}
                </div>
              </div>
            ) : (
              <div>
                <Divider className="my-3" />
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="arrow-square-out"
                    onClick={() => onWorkspaceOpen(activeChangeCase.id)}
                    data-testid="portfolio-open-change-workspace"
                  >
                    {activeChangeCase.status === "open" ||
                    activeChangeCase.status === "changes-required"
                      ? "Open change workspace"
                      : "View change workspace"}
                  </Button>
                  {discard}
                </div>
              </div>
            )}
          </Alert>
        ) : null}
      </section>
    </>
  );
}
